import { useState, useEffect, useCallback } from "react";
import { formatDistanceToNow } from "date-fns";
import { getLastHealthSyncTimestamp } from "@/lib/appleHealth";

/**
 * Returns the last health sync timestamp and a human-readable "X ago" string.
 * Updates when health-sync-complete fires. Refreshes display every minute.
 */
export function useLastHealthSyncTime(userId: string | null) {
  const [timestamp, setTimestamp] = useState<number | null>(() =>
    userId ? getLastHealthSyncTimestamp(userId) : null
  );
  const [, setTick] = useState(0);

  const refresh = useCallback(() => {
    if (userId) setTimestamp(getLastHealthSyncTimestamp(userId));
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setTimestamp(null);
      return;
    }
    setTimestamp(getLastHealthSyncTimestamp(userId));
    const onSync = () => refresh();
    window.addEventListener("health-sync-complete", onSync);
    const interval = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => {
      window.removeEventListener("health-sync-complete", onSync);
      clearInterval(interval);
    };
  }, [userId, refresh]);

  const formatted =
    timestamp != null
      ? formatDistanceToNow(timestamp, { addSuffix: true })
      : null;

  return { timestamp, formatted };
}
